import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import type { Feature, LineString, FeatureCollection } from 'geojson';
import { useQuery } from '@tanstack/react-query';
import { getDevices, getTrip } from '../api/endpoints';
import type { Trip, TripPoint } from '../types';
import { formatDateTime, formatDuration } from '../utils/dates';
import { speedToColor } from '../utils/speed';

const BRAND = '#00955F';

const OSM_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: 'raster' as const,
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a>',
      maxzoom: 19,
    },
  },
  layers: [{ id: 'osm-tiles', type: 'raster' as const, source: 'osm' }],
};

const REPLAY_SPEEDS = [1, 2, 5] as const;
type ReplaySpeed = (typeof REPLAY_SPEEDS)[number];

const selectStyle: React.CSSProperties = {
  height: 36, padding: '0 12px',
  border: '1px solid var(--color-border-strong)', borderRadius: 9,
  fontSize: 13, background: 'var(--color-bg-base)', color: 'var(--color-text)',
  outline: 'none', fontFamily: 'inherit',
};
const inputDateStyle: React.CSSProperties = {
  height: 36, padding: '0 12px',
  border: '1px solid var(--color-border-strong)', borderRadius: 9,
  fontSize: 13, background: 'var(--color-bg-base)', color: 'var(--color-text)',
  outline: 'none', fontFamily: 'inherit', colorScheme: 'light',
};

export default function HistoryPage() {
  const [searchParams] = useSearchParams();
  const initialDeviceId = searchParams.get('deviceId') ? Number(searchParams.get('deviceId')) : null;

  const [deviceId, setDeviceId] = useState<number | null>(initialDeviceId);
  const [fromDate, setFromDate] = useState<string>(() => {
    const d = new Date(Date.now() - 24 * 3600 * 1000);
    return d.toISOString().slice(0, 16);
  });
  const [toDate, setToDate] = useState<string>(() => new Date().toISOString().slice(0, 16));

  const [replayProgress, setReplayProgress] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [replaySpeed, setReplaySpeed] = useState<ReplaySpeed>(1);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const replayMarkerRef = useRef<maplibregl.Marker | null>(null);
  const replayTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const progressRef = useRef(0);
  const startMarkersRef = useRef<maplibregl.Marker[]>([]);

  const { data: devices = [] } = useQuery({ queryKey: ['devices'], queryFn: getDevices });

  useEffect(() => {
    if (!deviceId && devices.length > 0) setDeviceId(devices[0].id);
  }, [devices, deviceId]);

  const { data: trip, isFetching, refetch } = useQuery({
    queryKey: ['trip', deviceId, fromDate, toDate],
    queryFn: () => getTrip(deviceId!, new Date(fromDate).toISOString(), new Date(toDate).toISOString()),
    enabled: !!deviceId,
  });

  const maxSpeedKmh = useMemo(
    () => trip?.points.reduce((max, p) => Math.max(max, p.speedKmh ?? 0), 0) ?? 0,
    [trip],
  );

  useEffect(() => {
    if (!mapContainerRef.current) return;
    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: OSM_STYLE,
      center: [-47.9292, -15.7801],
      zoom: 4,
      attributionControl: { compact: false },
    });
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !trip?.points.length) return;
    const onLoad = () => renderTripOnMap(map, trip);
    if (map.loaded()) onLoad();
    else map.once('load', onLoad);
  }, [trip]);

  function renderTripOnMap(map: MapLibreMap, tripData: Trip) {
    ['trip-line'].forEach((id) => { if (map.getLayer(id)) map.removeLayer(id); });
    ['trip-route'].forEach((id) => { if (map.getSource(id)) map.removeSource(id); });
    startMarkersRef.current.forEach((m) => m.remove());
    startMarkersRef.current = [];
    if (replayMarkerRef.current) { replayMarkerRef.current.remove(); replayMarkerRef.current = null; }

    const points = tripData.points;
    const coords = points.map((p) => [p.lon, p.lat] as [number, number]);

    const features: Feature<LineString>[] = points.slice(1).map((p, i) => ({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: [coords[i], coords[i + 1]] },
      properties: { speed: p.speedKmh ?? 0, color: speedToColor(p.speedKmh ?? 0) },
    }));
    const fc: FeatureCollection<LineString> = { type: 'FeatureCollection', features };

    map.addSource('trip-route', { type: 'geojson', data: fc });
    map.addLayer({
      id: 'trip-line', type: 'line', source: 'trip-route',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': ['get', 'color'], 'line-width': 3.5, 'line-opacity': 0.9 },
    });

    if (coords.length > 0) {
      const startMarker = new maplibregl.Marker({ color: BRAND })
        .setLngLat(coords[0]).setPopup(new maplibregl.Popup().setText('Início')).addTo(map);
      const endMarker = new maplibregl.Marker({ color: '#D32020' })
        .setLngLat(coords[coords.length - 1]).setPopup(new maplibregl.Popup().setText('Fim')).addTo(map);
      startMarkersRef.current = [startMarker, endMarker];
    }

    if (coords.length > 1) {
      const bounds = coords.reduce(
        (b, c) => b.extend(c as [number, number]),
        new maplibregl.LngLatBounds(coords[0], coords[0]),
      );
      map.fitBounds(bounds, { padding: 60, maxZoom: 16 });
    }

    const markerEl = document.createElement('div');
    markerEl.style.cssText = `
      width: 28px; height: 28px;
      background: #fff;
      border: 3px solid ${BRAND};
      border-radius: 50%;
      box-shadow: 0 0 0 3px rgba(0,149,95,0.2), 0 4px 12px rgba(0,0,0,0.2);
    `;
    replayMarkerRef.current = new maplibregl.Marker({ element: markerEl })
      .setLngLat(coords[0]).addTo(map);
  }

  useEffect(() => {
    if (!isPlaying || !trip?.points.length) return;
    const totalPoints = trip.points.length;
    replayTimerRef.current = setInterval(() => {
      progressRef.current = Math.min(progressRef.current + replaySpeed / totalPoints, 1);
      setReplayProgress(progressRef.current * 100);
      const idx = Math.floor(progressRef.current * (totalPoints - 1));
      const pos = trip.points[idx];
      if (replayMarkerRef.current && pos) {
        replayMarkerRef.current.setLngLat([pos.lon, pos.lat]);
        mapRef.current?.panTo([pos.lon, pos.lat], { duration: 200 });
      }
      if (progressRef.current >= 1) { setIsPlaying(false); clearInterval(replayTimerRef.current!); }
    }, 100);
    return () => { if (replayTimerRef.current) clearInterval(replayTimerRef.current); };
  }, [isPlaying, replaySpeed, trip]);

  function handleSliderChange(value: number) {
    if (!trip?.points.length) return;
    const pct = value / 100;
    progressRef.current = pct;
    setReplayProgress(value);
    const idx = Math.floor(pct * (trip.points.length - 1));
    const pos = trip.points[idx];
    if (replayMarkerRef.current && pos) replayMarkerRef.current.setLngLat([pos.lon, pos.lat]);
  }

  function togglePlay() {
    if (replayProgress >= 100) { progressRef.current = 0; setReplayProgress(0); }
    setIsPlaying((p) => !p);
  }

  const currentPoint: TripPoint | undefined =
    trip?.points[Math.floor((replayProgress / 100) * ((trip?.points.length ?? 1) - 1))];

  return (
    <div className="absolute inset-0 flex flex-col page-enter" style={{ background: 'var(--color-bg-base)' }}>
      {/* Controls bar */}
      <div style={{ background: '#fff', borderBottom: '1px solid var(--color-border)', padding: '12px 20px', flexShrink: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--color-text-dim)', marginBottom: 5 }}>
              Veículo
            </label>
            <select value={deviceId ?? ''} onChange={(e) => setDeviceId(Number(e.target.value))} style={selectStyle}>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>{d.label || d.imei}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--color-text-dim)', marginBottom: 5 }}>
              De
            </label>
            <input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputDateStyle} />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.14em', color: 'var(--color-text-dim)', marginBottom: 5 }}>
              Até
            </label>
            <input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputDateStyle} />
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching || !deviceId}
            style={{
              height: 36, padding: '0 18px',
              background: BRAND, color: '#fff',
              fontSize: 13, fontWeight: 700, borderRadius: 9, border: 'none',
              display: 'flex', alignItems: 'center', gap: 8,
              boxShadow: 'var(--shadow-brand)',
              opacity: isFetching || !deviceId ? 0.5 : 1,
            }}
          >
            {isFetching ? (
              <svg style={{ animation: 'spin 1s linear infinite' }} width={15} height={15} fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.3)" strokeWidth="4" />
                <path fill="#fff" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : (
              <svg width={15} height={15} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
            )}
            Buscar
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Map */}
        <div style={{ position: 'relative', flex: 1 }}>
          <div ref={mapContainerRef} style={{ position: 'absolute', inset: 0 }} />

          {/* Speed legend */}
          <div className="card-glass" style={{
            position: 'absolute', bottom: 20, left: 16, zIndex: 10,
            padding: '10px 14px', borderRadius: 12,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--color-text-muted)', marginBottom: 6 }}>
              Velocidade
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 56, height: 6, borderRadius: 9999, background: 'linear-gradient(to right, #00955F, #eab308, #D32020)' }} />
              <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>0 — 90+ km/h</span>
            </div>
          </div>
        </div>

        {/* Stats + replay panel */}
        <aside className="scrollbar-thin" style={{
          width: 272, borderLeft: '1px solid var(--color-border)',
          background: '#fff', display: 'flex', flexDirection: 'column',
          overflow: 'auto', flexShrink: 0,
        }}>
          {trip ? (
            <>
              <div style={{ padding: 16, borderBottom: '1px solid var(--color-border)' }}>
                <div className="t-label" style={{ marginBottom: 12 }}>Estatísticas</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <StatCard label="Distância" value={`${trip.distanceKm.toFixed(2)} km`} />
                  <StatCard label="Duração" value={formatDuration(trip.durationS)} />
                  <StatCard label="Vel. Máx." value={`${maxSpeedKmh.toFixed(1)} km/h`} />
                  <StatCard label="Registros" value={String(trip.points.length)} />
                </div>
              </div>

              <div style={{ padding: 16 }}>
                <div className="t-label" style={{ marginBottom: 12 }}>Replay</div>

                <input
                  type="range" min={0} max={100} step={0.1}
                  value={replayProgress}
                  onChange={(e) => handleSliderChange(Number(e.target.value))}
                  style={{ width: '100%', accentColor: BRAND, marginBottom: 6 }}
                />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <span style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>{replayProgress.toFixed(0)}%</span>
                  <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {currentPoint ? formatDateTime(currentPoint.deviceTime) : '—'}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={togglePlay}
                    disabled={!trip.points.length}
                    style={{
                      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                      padding: '9px 12px', background: BRAND, color: '#fff',
                      borderRadius: 10, border: 'none', fontSize: 13, fontWeight: 700,
                      boxShadow: 'var(--shadow-brand)',
                      opacity: !trip.points.length ? 0.5 : 1,
                    }}
                  >
                    {isPlaying ? (
                      <>
                        <svg width={14} height={14} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                        </svg>
                        Pausar
                      </>
                    ) : (
                      <>
                        <svg width={14} height={14} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                        {replayProgress >= 100 ? 'Reiniciar' : 'Play'}
                      </>
                    )}
                  </button>

                  <div style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', border: '1px solid var(--color-border-strong)' }}>
                    {REPLAY_SPEEDS.map((s) => (
                      <button
                        key={s}
                        onClick={() => setReplaySpeed(s)}
                        style={{
                          padding: '9px 10px', fontSize: 11, fontWeight: 700, border: 'none',
                          background: replaySpeed === s ? BRAND : 'var(--color-bg-base)',
                          color: replaySpeed === s ? '#fff' : 'var(--color-text-dim)',
                        }}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <div>
                <div style={{
                  width: 52, height: 52, borderRadius: 14, marginBottom: 16,
                  background: 'var(--color-bg-sunken)', marginInline: 'auto',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width={26} height={26} fill="none" viewBox="0 0 24 24" stroke="var(--color-text-dim)" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p style={{ fontSize: 13, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
                  Selecione um período e clique em{' '}
                  <span style={{ color: BRAND, fontWeight: 700 }}>Buscar</span>{' '}
                  para visualizar o histórico.
                </p>
              </div>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div style={{
      background: 'var(--color-bg-base)', borderRadius: 10, padding: '10px 12px',
      border: '1px solid var(--color-border)',
    }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--color-text)', fontFamily: 'var(--font-display)', lineHeight: 1 }}>
        {value}
      </div>
      <div style={{ fontSize: 11, color: 'var(--color-text-dim)', marginTop: 4, fontWeight: 600 }}>
        {label}
      </div>
    </div>
  );
}
